import React, { useContext, useState, useEffect } from 'react';
import './ExploreMenu.css';
import { useTranslation } from 'react-i18next';
import { StoreContext } from '../../Context/StoreContext';
import ImageComponent from '../common/Image';
import MenuShimmer from '../common/MenuShimmer';

// Utility function to ensure consistent string keys for React
const getStringKey = (id, fallback = '') => {
    if (!id) return fallback;
    if (typeof id === 'string') return id;
    
    // Handle populated MongoDB documents
    if (typeof id === 'object' && id._id) {
        return id._id.toString();
    }
    
    if (id.toString && typeof id.toString === 'function') return id.toString();
    return String(id) || fallback;
};

const ExploreMenu = ({ category, setCategory, hideTitle = false, restaurantId, searchQuery: localSearchQuery }) => {
  const { t } = useTranslation();
  const { menu_list, food_list, searchQuery: globalSearchQuery, isMenuLoading } = useContext(StoreContext);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchActive, setSearchActive] = useState(false);
  
  // Use local search query if provided (for restaurant-specific search), otherwise use global
  const activeSearchQuery = localSearchQuery !== undefined ? localSearchQuery : globalSearchQuery;
  
  // Update filtered categories when search query changes
  useEffect(() => {
    setSearchActive(activeSearchQuery !== "");
    
    if (!activeSearchQuery) {
      setFilteredCategories([]);
      return;
    }
    
    // Find which categories have food items matching the search
    const matchingCategories = new Set();
    
    // Always add "All" as a matching category when searching
    matchingCategories.add("All");
    
    // Check each food item for a match
    food_list.forEach(item => {
      // Filter by restaurant if restaurantId is provided
      if (restaurantId && item.restaurant !== restaurantId) {
        return;
      }
      
      // Check if item matches search query
      if (
        item.name?.toLowerCase().includes(activeSearchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(activeSearchQuery.toLowerCase())
      ) {
        // Add the category of this item to matching categories
        // Priority: populated menu data > normalized category > other fallbacks
        if (item.menu && typeof item.menu === 'object') {
          if (item.menu.name) {
            matchingCategories.add(item.menu.name);
          }
          if (item.menu.category) {
            matchingCategories.add(item.menu.category);
          }
        } else if (item.category) {
          matchingCategories.add(item.category);
        }
        
        // Also check alternative category fields for backward compatibility
        if (item.categoryName) {
          matchingCategories.add(item.categoryName);
        }
        
        // Handle categories array if present
        if (item.categories) {
          // Ensure it's an array before using array methods
          if (Array.isArray(item.categories)) {
            item.categories.forEach(cat => {
              if (typeof cat === 'string') {
                matchingCategories.add(cat);
              } else if (cat && typeof cat === 'object' && cat.name) {
                matchingCategories.add(cat.name);
              }
            });
          } 
          // If it's not an array but a string, add it directly
          else if (typeof item.categories === 'string') {
            matchingCategories.add(item.categories);
          }
          // Log unexpected types for debugging
          else {
            console.log("Unexpected categories type in food item:", 
              { id: item._id, name: item.name, categoriesType: typeof item.categories, categories: item.categories });
          }
        }
      }
    });
    
    setFilteredCategories(Array.from(matchingCategories));
  }, [activeSearchQuery, food_list, restaurantId]);
  
  // Check if a category has food items matching the search
  const checkCategoryMatches = (categoryName) => {
    if (!searchActive) return true;
    return filteredCategories.includes(categoryName) || categoryName === "All";
  };
  
  // Handle category click
  const handleCategoryClick = (name) => {
    setCategory(name);
    
    // Scroll to food display section
    const foodDisplay = document.getElementById('food-display');
    if (foodDisplay) {
      const yOffset = -80; // Add some offset for header
      const y = foodDisplay.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };
  
  return (
    <div className='explore-menu' id='explore-menu'>
      {!hideTitle && (
        <>
          <h1>Explore Our Menu</h1>
          <p className='explore-menu-text'>
            Experience a variety of delicious dishes from our restaurants. 
            Browse by category or search for your favorites.
          </p>
        </>
      )}
      <div className='explore-menu-list'>
        {isMenuLoading ? (
          <>
            {/* Always include 'All' option */}
            <div 
              onClick={() => handleCategoryClick('All')} 
              className={`explore-menu-list-item ${category === 'All' ? 'active' : ''}`}
            >
              <ImageComponent 
                src={null}
                alt="All Categories" 
                className={category === 'All' ? "active" : ""}
                width={80}
                height={80}
                fallback={`data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"><circle cx="25" cy="25" r="25" fill="%23f0f0f0"/><text x="25" y="32" font-family="Arial" font-size="24" text-anchor="middle" fill="%23333">A</text></svg>`}
              />
              <p>All</p>
            </div>
            
            {/* Show shimmer effects for menu categories */}
            {Array.from({ length: 6 }, (_, index) => (
              <MenuShimmer key={`menu-shimmer-${index}`} />
            ))}
          </>
        ) : (
          <>
            {/* Always include 'All' option */}
            <div 
              onClick={() => handleCategoryClick('All')} 
              className={`explore-menu-list-item ${category === 'All' ? 'active' : ''}`}
            >
              <ImageComponent 
                src={null}
                alt="All Categories" 
                className={category === 'All' ? "active" : ""}
                width={80}
                height={80}
                fallback={`data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"><circle cx="25" cy="25" r="25" fill="%23f0f0f0"/><text x="25" y="32" font-family="Arial" font-size="24" text-anchor="middle" fill="%23333">A</text></svg>`}
              />
              <p>All</p>
            </div>
            
            {/* Display menu categories */}
            {menu_list
          .filter(item => {
            // If restaurantId is provided, only show categories for that restaurant
            if (restaurantId && item.restaurant !== restaurantId) {
              return false;
            }
            return true;
          })
          .map((item, index) => {
            // Check if this category has any matching food items when search is active
            const hasMatches = !searchActive || filteredCategories.includes(item.name);
            
            return (
              <div 
                onClick={() => handleCategoryClick(item.name)} 
                key={getStringKey(item._id, `menu-${index}`)} 
                className={`explore-menu-list-item ${hasMatches ? '' : 'no-matches'}`}
              >
                <ImageComponent 
                  src={item.image || `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"><circle cx="25" cy="25" r="25" fill="%23f0f0f0"/><text x="25" y="32" font-family="Arial" font-size="24" text-anchor="middle" fill="%23333">${item.name.charAt(0)}</text></svg>`}
                  alt={item.name} 
                  className={category === item.name ? "active" : ""}
                  width={80}
                  height={80} 
                  fallback={`data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"><circle cx="25" cy="25" r="25" fill="%23f0f0f0"/><text x="25" y="32" font-family="Arial" font-size="24" text-anchor="middle" fill="%23333">${item.name.charAt(0)}</text></svg>`}
                />
                <p>{item.name}</p>
              </div>
            );
          })}
          </>
        )}
      </div>
      <hr />
    </div>
  );
};

export default ExploreMenu;